import dayjs from 'dayjs';
import { useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useLocalStorage } from 'usehooks-ts';

import { calendarRangeKey } from '@/consts/localStorageKeys';

export type Option = 'today' | 'last2Week' | 'last1Week' | 'last1Month' | 'all' | 'custom';

/** 프리셋 일수. 시작·종료일을 모두 포함해 센다 */
const PRESET_DAYS = {
  last1Week: 7,
  last2Week: 14,
  last1Month: 30,
} as const;

export const useCalendar = ({
  range,
  setRange,
  closeModal,
  storageKey = calendarRangeKey,
  minRangeDays,
  latestDate,
}: {
  range: DateRange | undefined;
  setRange: (range: DateRange | undefined) => void;
  closeModal?: () => void;
  /** 프리셋 선택 상태 저장 키. 화면마다 다른 키를 주면 서로 영향을 주지 않음 */
  storageKey?: string;
  /** 선택 가능한 최소 일수. 주면 클릭마다 범위를 새로 시작한다 */
  minRangeDays?: number;
  /** 프리셋의 종료일. 미지정 시 오늘 */
  latestDate?: Date;
}) => {
  const [calendarRange, setCalendarRange] = useState<DateRange | undefined>(range);
  const [currentOption, setCurrentOption] = useLocalStorage<Option>(storageKey, 'all');
  const applyPreset = (option: keyof typeof PRESET_DAYS) => {
    const end = dayjs(latestDate ?? new Date());
    setCalendarRange({
      from: end.subtract(PRESET_DAYS[option] - 1, 'day').toDate(),
      to: end.toDate(),
    });
    setCurrentOption(option);
  };

  const setLast1Week = () => applyPreset('last1Week');
  const setLast2Week = () => applyPreset('last2Week');
  const setLast1Month = () => applyPreset('last1Month');

  const setAll = () => {
    setCalendarRange({
      from: undefined,
      to: undefined,
    });
    setCurrentOption('all');
  };

  const estimateCurrentOptionFromRange = (range: DateRange | undefined): Option => {
    if (!range?.from && !range?.to) return 'all';

    const toDate = range.to ? dayjs(range.to) : dayjs();
    const fromDate = range.from ? dayjs(range.from) : dayjs();

    const days = toDate.diff(fromDate, 'day') + 1;

    if (days === 1) return 'today';
    if (days === PRESET_DAYS.last1Week) return 'last1Week';
    if (days === PRESET_DAYS.last2Week) return 'last2Week';
    if (days === PRESET_DAYS.last1Month) return 'last1Month';

    return 'custom';
  };

  const selectedDays = (target: DateRange | undefined) =>
    target?.from && target.to ? dayjs(target.to).diff(target.from, 'day') + 1 : 0;

  // min 이 있는 캘린더는 addToRange 가 기존 범위를 늘리기만 해서 안쪽 날짜를 다시 고를 수 없다
  const customSetRange = (next: DateRange | undefined, selectedDay?: Date) => {
    const restart = !!minRangeDays && !!selectedDay && !!calendarRange?.from && !!calendarRange.to;
    setCalendarRange(restart ? { from: selectedDay, to: undefined } : next);
    setCurrentOption('custom');
  };

  const canConfirm = !minRangeDays || selectedDays(calendarRange) >= minRangeDays;
  const isPicking = !!calendarRange?.from && !calendarRange.to;

  const closeCalendar = () => {
    setCalendarRange(range);
    setCurrentOption(estimateCurrentOptionFromRange(range));
    if (closeModal) {
      closeModal();
    }
  };

  const confirmRange = () => {
    if (!canConfirm) return;

    const { from, to } = calendarRange ?? {};

    if (!to) {
      setRange({
        from,
        to: from,
      });
      if (closeModal) {
        closeModal();
      }

      return;
    }

    setRange(calendarRange);
    if (closeModal) {
      closeModal();
    }
  };

  return {
    currentOption,
    calendarRange,
    canConfirm,
    isPicking,
    selectedDays: selectedDays(calendarRange),
    setLast1Week,
    setLast2Week,
    setLast1Month,
    setAll,
    customSetRange,
    closeCalendar,
    confirmRange,
  };
};
